import React, { useState, useEffect, useRef, useCallback, memo, useMemo } from 'react';
import { 
  Box,
  Paper,
  Typography,
  Grid,
  FormHelperText,
  InputLabel,
  FormControl,
  Card,
  CardContent,
  Stack,
  Chip,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTheme } from '@mui/material/styles';
import { Campaign, CampaignDates } from '../../../../types/Campaign';
import AIService from '../../../../services/AIService';
import {
  CalendarMonth,
  ArrowForward,
  Info as InfoIcon,
  AutoAwesome
} from '@mui/icons-material';

// Define interfaces for props
interface DatesProps {
  campaign: Campaign;
  onSave: (updatedCampaign: Partial<Campaign>) => Promise<Campaign>;
  onPendingChanges: (changes: Partial<Campaign>) => void;
}

// Timeline event for visualization
interface TimelineEvent {
  id: string;
  name: string;
  field: string;
  tooltip: string;
  date: Date | null;
  color?: string;
}

// Used for our component including any extra UI-only fields
interface FormDates {
  applications_open: string;
  applications_close: string;
  match_notification_date: string;
  matches_finalized: string;
  kickoff: string;
  closing: string;
  feedback_deadline: string;
}

// Memoize the CustomDatePicker to prevent unnecessary rerenders
const CustomDatePicker = memo(({
  value,
  onChange,
  id,
  onBlur,
  minDate
}: {
  value: Date | null;
  onChange: (date: Date | null) => void;
  id: string;
  onBlur: () => void;
  minDate?: Date | null;
}) => {
  // Ensure onChange actually gets called with the date
  const handleChange = (newDate: Date | null) => {
    if (onChange) {
      // Validate the date before processing
      if (newDate && !isNaN(newDate.getTime())) {
        onChange(newDate);
        // Also trigger the blur handler to ensure an update happens
        // This ensures that each date selection triggers at least one save
        setTimeout(onBlur, 100);
      } else if (newDate === null) {
        // Handle clearing the date
        onChange(null);
        setTimeout(onBlur, 100);
      }
    }
  };

  return (
    <DatePicker
      value={value}
      onChange={handleChange}
      onClose={onBlur}
      minDate={minDate || undefined}
      slotProps={{
        textField: {
          fullWidth: true,
          variant: 'outlined',
          id: id,
          onBlur: onBlur,
          sx: { bgcolor: 'background.paper' }
        }
      }}
    />
  );
});

const Dates: React.FC<DatesProps> = ({ campaign, onSave, onPendingChanges }) => {
  const theme = useTheme();
  // Track if an update is in progress to prevent duplicate saves
  const isUpdatingRef = useRef(false);
  // Store campaign ID to detect if the campaign changes
  const campaignIdRef = useRef<string | null>(null);

  // First initialize with empty values
  const [formData, setFormData] = useState<FormDates>({
    applications_open: '',
    applications_close: '',
    match_notification_date: '',
    matches_finalized: '',
    kickoff: '',
    closing: '',
    feedback_deadline: ''
  });

  // Track current UI state
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Track temporary changes before committing to backend
  const [tempDates, setTempDates] = useState<Record<string, string>>({});

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Load campaign dates when the campaign changes
  useEffect(() => {
    if (!campaign?.id) return;

    // Avoid reloading if campaign ID is the same
    if (campaignIdRef.current === campaign.id) return;

    // Update the ref to track the current campaign ID
    campaignIdRef.current = campaign.id;

    if (campaign?.dates) {
      // Extract dates from campaign
      setFormData({
        applications_open: campaign.dates.applications_open || '',
        applications_close: campaign.dates.applications_close || '',
        match_notification_date: campaign.dates.match_notification_date || '',
        matches_finalized: campaign.dates.matches_finalized || '',
        kickoff: campaign.dates.kickoff || '',
        closing: campaign.dates.closing || '',
        feedback_deadline: campaign.dates.feedback_deadline || ''
      });
    }
  }, [campaign?.id, campaign?.dates]);

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Save campaign dates to backend
  const saveCampaignDates = useCallback(async (datesToSave: FormDates) => {
    if (!onSave || isUpdatingRef.current || !Object.keys(datesToSave).length) return;

    try {
      isUpdatingRef.current = true;
      setSaveStatus('saving');

      // Format date strings to only include the date part (YYYY-MM-DD)
      const formatDateString = (dateStr: string | null): string | null => {
        if (!dateStr) return null;
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        return date.toISOString().split('T')[0]; // Get only YYYY-MM-DD
      };

      // Convert form data to CampaignDates with proper null handling and date formatting
      const dates: CampaignDates = {
        applications_open: formatDateString(datesToSave.applications_open),
        applications_close: formatDateString(datesToSave.applications_close),
        match_notification_date: formatDateString(datesToSave.match_notification_date),
        matches_finalized: formatDateString(datesToSave.matches_finalized),
        kickoff: formatDateString(datesToSave.kickoff),
        closing: formatDateString(datesToSave.closing),
        feedback_deadline: formatDateString(datesToSave.feedback_deadline)
      };

      // Only send the dates object - do NOT include id or organization_id
      // The parent component will handle adding these if needed
      const campaignUpdate: Partial<Campaign> = {
        dates: dates
      };

      console.log('Saving campaign with dates:', campaignUpdate);
      await onSave(campaignUpdate);
      setSaveStatus('saved');
    } catch (error) {
      console.error('Error saving campaign dates', error);
      setSaveStatus('error');
    } finally {
      // Reset the updating flag
      isUpdatingRef.current = false;
    }
  }, [onSave]);

  // Store date temporarily when the picker changes
  const handleDateChange = useCallback((field: keyof FormDates) => {
    return (date: Date | null) => {
      console.log(`Date changed for ${field}:`, date);
      
      // Update temp dates for immediate UI feedback
      setTempDates(prev => ({
        ...prev,
        [field]: date ? date.toISOString().split('T')[0] : ''
      }));
      
      // Instead of saving on every change, just store the pending change
      // Create a properly structured Campaign object with the single changed field
      const dateValue = date ? date.toISOString().split('T')[0] : null;
      
      // Create a proper Partial<Campaign> with the current formData as a baseline
      // and only update the specific field that changed
      const currentDates = { ...formData };
      
      // Update the specific field
      const updatedDates = {
        ...currentDates,
        [field]: dateValue || ''
      };
      
      // Register the pending change with the parent component
      onPendingChanges({
        dates: {
          applications_open: updatedDates.applications_open || null,
          applications_close: updatedDates.applications_close || null,
          match_notification_date: updatedDates.match_notification_date || null,
          matches_finalized: updatedDates.matches_finalized || null,
          kickoff: updatedDates.kickoff || null,
          closing: updatedDates.closing || null,
          feedback_deadline: updatedDates.feedback_deadline || null
        }
      });
    };
  }, [onPendingChanges, formData]);

  // Commit the date and save when the field loses focus or picker closes
  const handleDateBlur = useCallback((field: keyof FormDates) => {
    return () => {
      console.log(`Blur for ${field}`);
      const date = tempDates[field];

      // Only proceed if we have a temporary date stored
      if (date !== undefined) {
        try {
          // Validate the date is valid before processing
          if (date && !isNaN(new Date(date).getTime())) {
            // Always update on blur to ensure changes are saved
            const newFormData = {
              ...formData,
              [field]: date
            };

            setFormData(newFormData);

            // Save to backend with the exact same structure - ensure snake_case format for API
            // Save the entire dates object to maintain API consistency
            console.log('Saving full dates object to maintain structure');
            saveCampaignDates(newFormData);
          } else if (date === '') {
            // Handle clearing the date
            const newFormData = {
              ...formData,
              [field]: ''
            };

            setFormData(newFormData);
            saveCampaignDates(newFormData);
          }
        } catch (error) {
          console.error('Error processing date:', error);
          // Handle invalid date gracefully
        }

        // Clear the temp date
        setTempDates(prev => {
          const updated = { ...prev };
          delete updated[field];
          return updated;
        });
      }
    };
  }, [tempDates, formData, saveCampaignDates]);

  // Compute timeline events based on form data
  const timelineEvents: TimelineEvent[] = [
    { id: 'event-1', name: 'Applications Open', field: 'applications_open', tooltip: 'Start date for accepting applications from mentors and mentees', date: formData.applications_open ? new Date(formData.applications_open) : null, color: theme.palette.primary.main },
    { id: 'event-2', name: 'Applications Close', field: 'applications_close', tooltip: 'Deadline for submitting applications', date: formData.applications_close ? new Date(formData.applications_close) : null, color: theme.palette.primary.main },
    { id: 'event-3', name: 'Match Notification', field: 'match_notification_date', tooltip: 'Date when matches are notified to participants', date: formData.match_notification_date ? new Date(formData.match_notification_date) : null, color: theme.palette.warning.main },
    { id: 'event-4', name: 'Matches Finalized', field: 'matches_finalized', tooltip: 'Deadline for participants to acknowledge matches', date: formData.matches_finalized ? new Date(formData.matches_finalized) : null, color: theme.palette.warning.main },
    { id: 'event-5', name: 'Kickoff', field: 'kickoff', tooltip: 'Official start date of the mentorship program when mentors and mentees begin working together', date: formData.kickoff ? new Date(formData.kickoff) : null, color: theme.palette.success.main },
    { id: 'event-6', name: 'Closing', field: 'closing', tooltip: 'Final date of the mentorship program, typically includes closing ceremonies or evaluations', date: formData.closing ? new Date(formData.closing) : null, color: theme.palette.success.main },
    { id: 'event-7', name: 'Feedback Deadline', field: 'feedback_deadline', tooltip: 'Deadline for submitting feedback', date: formData.feedback_deadline ? new Date(formData.feedback_deadline) : null, color: theme.palette.success.main },
  ];

  // Calculate time between events in days
  const getDaysBetween = (startEvent: TimelineEvent, endEvent: TimelineEvent): number | null => {
    if (!startEvent.date || !endEvent.date) {
      return null;
    }
    return Math.abs((endEvent.date.getTime() - startEvent.date.getTime()) / (1000 * 60 * 60 * 24));
  };

  const datePickerWrapperStyle = {
    marginBottom: '1rem',
    width: '100%'
  };

  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [generatingDates, setGeneratingDates] = useState(false);
  const [tempKickoff, setTempKickoff] = useState<Date | null>(null);
  const [tempClosing, setTempClosing] = useState<Date | null>(null);

  // Check if dates are valid for generation
  const datesAreValid = useMemo(() => {
    if (!tempKickoff || !tempClosing) return false;
    
    // Ensure closing date is after kickoff date
    return tempClosing > tempKickoff;
  }, [tempKickoff, tempClosing]);

  // Create an instance of AIService
  const aiService = useMemo(() => new AIService(), []);

  const handleOpenGenerateDialog = () => {
    // Initialize with current values if they exist
    if (formData.kickoff) {
      setTempKickoff(new Date(formData.kickoff));
    }
    if (formData.closing) {
      setTempClosing(new Date(formData.closing));
    }
    setOpenGenerateDialog(true);
  };

  const handleCloseGenerateDialog = () => {
    setOpenGenerateDialog(false);
  };

  const handleDialogSubmit = () => {
    handleGenerateDates();
  };

  const handleGenerateDates = async () => {
    if (!datesAreValid || !tempKickoff || !tempClosing) {
      setSnackbar({
        open: true,
        message: 'Please select valid kickoff and closing dates',
        severity: 'error'
      });
      return;
    }

    if (!campaign?.id || !campaign?.organization_id) {
      setSnackbar({
        open: true,
        message: 'Campaign information is missing',
        severity: 'error'
      });
      return;
    }

    try {
      setGeneratingDates(true);

      // Format dates as YYYY-MM-DD for the API
      const formatDateForApi = (date: Date): string => {
        return date.toISOString().split('T')[0]; // Get only YYYY-MM-DD
      };
      
      // Convert dates to YYYY-MM-DD format for the API
      const kickoffStr = formatDateForApi(tempKickoff);
      const closingStr = formatDateForApi(tempClosing);
      
      console.log(`Generating dates with kickoff=${kickoffStr}, closing=${closingStr}`);
      
      // Call the AI service to generate dates
      const response = await aiService.generateDates(
        campaign.organization_id,
        campaign.id,
        kickoffStr,
        closingStr
      );
      
      console.log('Generated dates:', response);
      
      // Close the dialog
      setOpenGenerateDialog(false);

      // Handle the response format from the AI service which returns YYYY-MM-DD formats
      // We maintain YYYY-MM-DD format for consistency
      const formatDates = (dateStr: string | null): string => {
        if (!dateStr) return '';
        return dateStr; // Already in YYYY-MM-DD format
      };
      
      // Update the form with the generated dates
      const newFormData: FormDates = {
        applications_open: formatDates(response.applications_open),
        applications_close: formatDates(response.applications_close),
        match_notification_date: formatDates(response.match_notification_date),
        matches_finalized: formatDates(response.matches_finalized),
        kickoff: formatDates(response.kickoff),
        closing: formatDates(response.closing),
        feedback_deadline: formatDates(response.feedback_deadline)
      };
      
      setFormData(newFormData);
      
      // Save the dates to the backend
      const campaignUpdate: Partial<Campaign> = {
        dates: newFormData
      };
      
      console.log('Saving generated dates to campaign:', campaignUpdate);
      await onSave(campaignUpdate);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Dates generated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error generating dates:', error);
      setSnackbar({
        open: true,
        message: 'Error generating dates: ' + (error instanceof Error ? error.message : 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setGeneratingDates(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Campaign Timeline
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Set all important dates for your mentorship campaign. These dates will be visible to participants
              and will help guide the campaign process.
            </Typography>
          </CardContent>
        </Card>

        {/* Visual Timeline */}
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 4,
            backgroundColor: theme.palette.background.default,
            borderRadius: 2
          }}
        >
          <Typography variant="h6" gutterBottom>
            Campaign Schedule
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This sequential timeline shows all events and the duration between them. Update the dates below to see changes reflected here.
          </Typography>

          <Stack
            direction="row"
            spacing={0}
            sx={{
              overflowX: 'auto',
              pb: 2,
              pt: 1,
              '&::-webkit-scrollbar': { height: '8px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.divider, borderRadius: '4px' }
            }}
          >
            {timelineEvents.map((event, index) => (
              <React.Fragment key={event.id}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    minWidth: 140,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                    borderLeft: `4px solid ${event.color || theme.palette.primary.main}`,
                    borderRadius: 1,
                    zIndex: 2
                  }}
                >
                  <CalendarMonth
                    fontSize="small"
                    sx={{
                      color: event.color || theme.palette.text.secondary,
                      mb: 0.5
                    }}
                  />
                  <Tooltip title={event.tooltip}>
                    <Typography variant="subtitle2" align="center" gutterBottom>
                      {event.name}
                    </Typography>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    color={event.date ? 'text.primary' : 'text.disabled'}
                    align="center"
                  >
                    {event.date ? event.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set'}
                  </Typography>
                </Paper>

                {/* Duration between events */}
                {index < timelineEvents.length - 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 1,
                      position: 'relative',
                      zIndex: 1,
                      minWidth: 70
                    }}
                  >
                    <Box sx={{
                      height: '2px',
                      bgcolor: theme.palette.divider,
                      width: '100%',
                      position: 'absolute'
                    }} />

                    {getDaysBetween(event, timelineEvents[index + 1]) !== null && (
                      <Chip
                        label={`${getDaysBetween(event, timelineEvents[index + 1])} days`}
                        size="small"
                        sx={{
                          bgcolor: theme.palette.background.paper,
                          fontSize: '0.65rem',
                          mt: 0.5
                        }}
                      />
                    )}

                    <ArrowForward
                      fontSize="small"
                      sx={{
                        color: theme.palette.text.secondary,
                        bgcolor: theme.palette.background.paper,
                        borderRadius: '50%',
                        p: 0.2,
                        boxSizing: 'content-box'
                      }}
                    />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Stack>
        </Paper>

        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Key Dates
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleOpenGenerateDialog} 
              startIcon={generatingDates ? <CircularProgress size={20} /> : <AutoAwesome />}
              disabled={generatingDates}
            >
              {generatingDates ? 'Generating...' : 'AI Generate Dates'}
            </Button>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {/* Application Phase (Blue) */}
              <Grid item xs={12} md={6}>
                <div style={datePickerWrapperStyle}>
                  <InputLabel shrink htmlFor="applications_open">
                    <Tooltip title="Start date for accepting applications from mentors and mentees">
                      <span>Applications Open <InfoIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: '0.85rem' }} /></span>
                    </Tooltip>
                  </InputLabel>
                  <CustomDatePicker
                    value={formData.applications_open ? new Date(formData.applications_open) : null}
                    onChange={handleDateChange('applications_open')}
                    onBlur={handleDateBlur('applications_open')}
                    id="applications_open"
                  />
                  <FormHelperText>Set when applications will open</FormHelperText>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div style={datePickerWrapperStyle}>
                  <InputLabel shrink htmlFor="applications_close">
                    <Tooltip title="Deadline after which no more applications will be accepted">
                      <span>Applications Close <InfoIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: '0.85rem' }} /></span>
                    </Tooltip>
                  </InputLabel>
                  <CustomDatePicker
                    value={formData.applications_close ? new Date(formData.applications_close) : null}
                    onChange={handleDateChange('applications_close')}
                    onBlur={handleDateBlur('applications_close')}
                    id="applications_close"
                  />
                  <FormHelperText>Set the application deadline</FormHelperText>
                </div>
              </Grid>

              {/* Matching Phase (Yellow) */}
              <Grid item xs={12} md={6}>
                <div style={datePickerWrapperStyle}>
                  <InputLabel shrink htmlFor="match_notification_date">
                    <Tooltip title="Date when matches are notified to participants">
                      <span>Match Notification <InfoIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: '0.85rem' }} /></span>
                    </Tooltip>
                  </InputLabel>
                  <CustomDatePicker
                    value={formData.match_notification_date ? new Date(formData.match_notification_date) : null}
                    onChange={handleDateChange('match_notification_date')}
                    onBlur={handleDateBlur('match_notification_date')}
                    id="match_notification_date"
                  />
                  <FormHelperText>Set the date when matches are notified to participants</FormHelperText>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div style={datePickerWrapperStyle}>
                  <InputLabel shrink htmlFor="matches_finalized">
                    <Tooltip title="Deadline for participants to acknowledge their matches - unacknowledged matches may be rematched after this date">
                      <span>Matches Finalized <InfoIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: '0.85rem' }} /></span>
                    </Tooltip>
                  </InputLabel>
                  <CustomDatePicker
                    value={formData.matches_finalized ? new Date(formData.matches_finalized) : null}
                    onChange={handleDateChange('matches_finalized')}
                    onBlur={handleDateBlur('matches_finalized')}
                    id="matches_finalized"
                  />
                  <FormHelperText>Set the deadline for participants to acknowledge their matches</FormHelperText>
                </div>
              </Grid>

              {/* Mentorship Phase (Green) */}
              <Grid item xs={12} md={6}>
                <div style={datePickerWrapperStyle}>
                  <InputLabel shrink htmlFor="kickoff">
                    <Tooltip title="Official start date of the mentorship program when mentors and mentees begin working together">
                      <span>Kickoff <InfoIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: '0.85rem' }} /></span>
                    </Tooltip>
                  </InputLabel>
                  <CustomDatePicker
                    value={formData.kickoff ? new Date(formData.kickoff) : null}
                    onChange={handleDateChange('kickoff')}
                    onBlur={handleDateBlur('kickoff')}
                    id="kickoff"
                  />
                  <FormHelperText>Set the official start date of the mentorship program</FormHelperText>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div style={datePickerWrapperStyle}>
                  <InputLabel shrink htmlFor="closing">
                    <Tooltip title="Final date of the mentorship program, typically includes closing ceremonies or evaluations">
                      <span>Closing <InfoIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: '0.85rem' }} /></span>
                    </Tooltip>
                  </InputLabel>
                  <CustomDatePicker
                    value={formData.closing ? new Date(formData.closing) : null}
                    onChange={handleDateChange('closing')}
                    onBlur={handleDateBlur('closing')}
                    id="closing"
                    minDate={formData.kickoff ? new Date(formData.kickoff) : null}
                  />
                  <FormHelperText>Set the final date of the mentorship program</FormHelperText>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div style={datePickerWrapperStyle}>
                  <InputLabel shrink htmlFor="feedback_deadline">
                    <Tooltip title="Deadline for participants to submit their feedback on the mentorship experience">
                      <span>Feedback Deadline <InfoIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: '0.85rem' }} /></span>
                    </Tooltip>
                  </InputLabel>
                  <CustomDatePicker
                    value={formData.feedback_deadline ? new Date(formData.feedback_deadline) : null}
                    onChange={handleDateChange('feedback_deadline')}
                    onBlur={handleDateBlur('feedback_deadline')}
                    id="feedback_deadline"
                  />
                  <FormHelperText>Set the date by which all participant feedback must be received</FormHelperText>
                </div>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Dialog open={openGenerateDialog} onClose={handleCloseGenerateDialog}>
          <DialogTitle>Generate Campaign Dates</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter the kickoff and closing dates for your campaign. We'll use AI to generate 
              a suggested timeline including application periods, matching dates, and feedback deadlines.
              Once generated, you can adjust any dates as needed.
            </DialogContentText>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel shrink htmlFor="dialog-kickoff">
                  Kickoff Date (when mentoring begins)
                </InputLabel>
                <DatePicker
                  value={tempKickoff}
                  onChange={(date) => setTempKickoff(date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      id: 'dialog-kickoff',
                      sx: { bgcolor: 'background.paper' }
                    }
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <InputLabel shrink htmlFor="dialog-closing">
                  Closing Date (when mentoring ends)
                </InputLabel>
                <DatePicker
                  value={tempClosing}
                  onChange={(date) => setTempClosing(date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      id: 'dialog-closing',
                      sx: { bgcolor: 'background.paper' }
                    }
                  }}
                  minDate={tempKickoff}
                />
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                We'll calculate the best dates for:
                <ul>
                  <li>Applications Open and Close</li>
                  <li>Match Notification and Matches Finalized</li>
                  <li>Feedback Deadline</li>
                </ul>
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseGenerateDialog}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={handleDialogSubmit} disabled={!datesAreValid}>Generate</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Save Status Indicator */}
        <Box sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1100,
          bgcolor: 'background.paper',
          p: 1,
          borderRadius: 1,
          boxShadow: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          {saveStatus === 'saving' && (
            <>
              <Typography variant="body2" color="text.secondary">Saving...</Typography>
            </>
          )}
          {saveStatus === 'saved' && (
            <>
              <Typography variant="body2" color="success.main">Changes saved</Typography>
            </>
          )}
          {saveStatus === 'error' && (
            <>
              <Typography variant="body2" color="error">Error saving</Typography>
            </>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default Dates;
